import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';

import { translate } from '@jsverse/transloco';
import { capitalize } from 'humanize-plus';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { ChartOptionConstants, Constants } from 'app/constants/constants';
import { QueryParams } from 'app/providers/api.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import {
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { ChartDisplayOptions } from '../../../../models';
import { ImpactMetric } from '../widget-impact/rating-field-selector-dialog/rating-field-selector-dialog.component';
import { WidgetPredictiveOptions } from '../widget-predictive/widget-predictive.component';

interface OptionForm {
  scoringStrategyRadios: FormControl<'Average' | 'Box' | 'NPS'>;
  boxDirectionSelect?: FormControl<'TOP' | 'BOTTOM'>;
  boxSizeSelect?: FormControl<'1' | '2' | '3' | '4' | '5' | '6' | '7'>;
  scaleMax: FormControl<number>;
  scaleToggle: FormControl<0 | 1>;
}

@Component({
  selector: 'app-predictive-breakdown-drivers-options-dialog',
  templateUrl: './predictive-breakdown-drivers-options-dialog.component.html',
  styleUrls: ['./predictive-breakdown-drivers-options-dialog.component.scss'],
  standalone: false,
})
export class PredictiveBreakdownDriversOptionsDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() public data: ChartDisplayOptions;
  @Input() public type: 'breakdown' | 'drivers' | 'rating';

  @Output() modalClosed: EventEmitter<any> = new EventEmitter<ChartDisplayOptions>();

  public formGroup: FormGroup<OptionForm>;
  public readonly displayGroups = [
    {
      id: 'Average',
      label: translate('widgets.options.average'),
    },
    {
      id: 'Box',
      label: translate('widgets.options.box'),
    },
    {
      id: 'NPS',
      label: translate('widgets.options.nps'),
    },
  ];

  boxDirectionDos: DropdownObject[] = ChartOptionConstants.BOXDIRECTIONDOS;
  boxSizeDos: DropdownObject[];

  public maxScaleStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  public isScaleToggled: boolean;
  public isBinaryField = false;
  public isBucketScoring = false;
  private payloadOptions: any;

  private unsub$: Subject<void> = new Subject<void>();

  get isFormValid(): boolean {
    // If binary field and Box is selected, form is invalid
    if (this.isBinaryField && this.formGroup?.controls.scoringStrategyRadios.value === 'Box') {
      return false;
    }
    // If bucket metric and Box is selected, form is invalid
    if (this.isBucketScoring && this.formGroup?.controls.scoringStrategyRadios.value === 'Box') {
      return false;
    }
    // If bucket metric and NPS is selected, form is invalid
    if (this.isBucketScoring && this.formGroup?.controls.scoringStrategyRadios.value === 'NPS') {
      return false;
    }
    return this.formGroup?.status === 'VALID';
  }

  constructor(
    private formBuilder: FormBuilder,
    private storageAccountService: StorageAccountService
  ) {}

  public ngOnInit() {
    // Use the stored impactMetric from payload (only for rating type)
    if (this.type === 'rating') {
      if (this.data.payload.impactMetric) {
        // New widgets with stored impactMetric
        this.isBinaryField = this.data.payload.impactMetric === ImpactMetric.BINARY;
        this.isBucketScoring = this.data.payload.impactMetric === ImpactMetric.BUCKETS;
      } else {
        // Legacy widgets: infer from other properties
        this.isBucketScoring =
          (this.data.payload.customBuckets && this.data.payload.customBuckets.length > 0) ||
          this.data.payload.selectedBucketIndex !== undefined;

        // For binary, we need to check metadata (async)
        if (this.data.ratingFieldName) {
          this.storageAccountService.groupFieldsSub
            .takeUntil(this.unsub$)
            .subscribe((accountMetadata) => {
              const currentField = accountMetadata?.metadata?.find(
                (f) => f.fieldName === this.data.ratingFieldName
              );
              this.isBinaryField = currentField?.metadata?.calculationType === 'binary';

              // Update controls if binary field detected after form creation
              if (this.isBinaryField && this.formGroup) {
                this.formGroup.controls.scaleMax.disable();
                this.formGroup.controls.scaleToggle.disable();
              }
            });
        }
      }
    }

    const numberInputValidatorFactory = (status: ErrorObject): ValidatorFn => {
      return (control: AbstractControl) => {
        if (control.value > 1000 || control.value < 2) {
          ErrorObjectUtils.setError(
            status,
            translate('widgets.options.error-must-be-between', { min: 2, max: 1000 })
          );
          return { error: 'minmax' };
        }

        ErrorObjectUtils.setNoError(status);
        return null;
      };
    };

    const payload = <WidgetPredictiveOptions>this.data.payload;
    const ciEquals = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;

    let scale: string[];
    switch (this.type) {
      case 'breakdown':
        scale = (payload.dependentRange ?? Constants.TWTRA_DEFAULT_DEPENDENT_RANGE).split(':');
        this.payloadOptions = {
          scoringStrategy: this.data.payload.breakdownScoringStrategy,
          boxDirection: this.data.payload.breakdownBoxDirection,
          boxSize: this.data.payload.breakdownBoxSize,
        };
        this.isScaleToggled = this.data.payload.dependentRange?.[0] !== '0';
        break;
      case 'drivers':
        scale = (payload.driversRange ?? Constants.TWTRA_DEFAULT_DRIVERS_RANGE).split(':');
        this.payloadOptions = {
          scoringStrategy: this.data.payload.driverScoringStrategy,
          boxDirection: this.data.payload.driverBoxDirection,
          boxSize: this.data.payload.driverBoxSize,
        };
        this.isScaleToggled = this.data.payload.driversRange?.[0] !== '0';
        break;
      case 'rating':
        scale = (payload.ratingScale ?? Constants.TWTRA_DEFAULT_DRIVERS_RANGE).split(':');
        this.payloadOptions = {
          scoringStrategy: this.data.payload.scoringStrategy,
          boxDirection: this.data.payload.boxDirection,
          boxSize: this.data.payload.boxSize,
        };
        this.isScaleToggled = this.data.payload.ratingScale?.[0] !== '0';
    }

    this.boxSizeDos =
      this.payloadOptions.boxDirection === 'BOTTOM'
        ? ChartOptionConstants.BOTTOMBOXSIZEDOS
        : ChartOptionConstants.TOPBOXSIZEDOS;

    const scaleMin = parseInt(scale[0], 10);
    const scaleMax = parseInt(scale[1], 10);

    this.formGroup = this.formBuilder.group(<OptionForm>{
      scoringStrategyRadios: new FormControl(
        this.displayGroups.find((dg) =>
          ciEquals(
            dg.id,
            this.payloadOptions.scoringStrategy ??
              capitalize(Constants.TWTRA_DEFAULT_SCORING_STRATEGY, true)
          )
        ).id
      ),
      boxDirectionSelect: new FormControl({
        value: this.boxDirectionDos.find((el) =>
          ciEquals(
            el.title,
            this.payloadOptions.boxDirection ??
              capitalize(Constants.TWTRA_DEFAULT_BOX_DIRECTION, true)
          )
        ).id,
        disabled: this.payloadOptions.scoringStrategy !== 'BOX' || this.isBinaryField,
      }),
      boxSizeSelect: new FormControl({
        value: this.payloadOptions.boxSize ?? Constants.TWTRA_DEFAULT_BOX_SIZE,
        disabled: this.payloadOptions.scoringStrategy !== 'BOX' || this.isBinaryField,
      }),
      scaleMax: new FormControl(
        { value: scaleMax, disabled: this.isBucketScoring || this.isBinaryField },
        numberInputValidatorFactory(this.maxScaleStatus)
      ),
      scaleToggle: new FormControl({
        value: scaleMin,
        disabled: this.isBucketScoring || this.isBinaryField,
      }),
    });

    this.formGroup.controls.scoringStrategyRadios.valueChanges
      .pipe(takeUntil(this.unsub$))
      .subscribe((value) => {
        // Prevent Box selection for binary fields or bucket metrics
        if (value === 'Box' && (this.isBinaryField || this.isBucketScoring)) {
          // Revert to Average
          setTimeout(() => {
            this.formGroup.controls.scoringStrategyRadios.setValue('Average', { emitEvent: false });
          });
        } else if (value === 'Box' && !this.isBinaryField && !this.isBucketScoring) {
          this.formGroup.controls.boxDirectionSelect.enable();
          this.formGroup.controls.boxSizeSelect.enable();
        } else {
          this.formGroup.controls.boxDirectionSelect.disable();
          this.formGroup.controls.boxSizeSelect.disable();
        }

        // Prevent NPS selection for bucket metric fields
        if (value === 'NPS' && this.isBucketScoring) {
          // Revert to Average
          setTimeout(() => {
            this.formGroup.controls.scoringStrategyRadios.setValue('Average', { emitEvent: false });
          });
        }
      });

    this.formGroup.controls.boxDirectionSelect.valueChanges
      .pipe(takeUntil(this.unsub$))
      .subscribe((value: string) => {
        if (value === 'BOTTOM') {
          this.boxSizeDos = ChartOptionConstants.BOTTOMBOXSIZEDOS;
        } else {
          this.boxSizeDos = ChartOptionConstants.TOPBOXSIZEDOS;
        }

        this.formGroup.patchValue({ boxSizeSelect: Constants.TWTRA_DEFAULT_BOX_SIZE });
      });
  }

  public onCancel() {
    this.modalClosed.emit(null);
  }

  public onSave() {
    this.data.needRefresh = true;

    const minScale = this.formGroup.value.scaleToggle;

    const params: QueryParams = {};
    // For binary fields, always force 0-100 scale
    // For bucket scoring, the range will be determined by the bucket selection
    let rangeParam = `${minScale}:${this.formGroup.value.scaleMax}`;
    if (this.isBinaryField) {
      rangeParam = '0:100';
    } else if (this.isBucketScoring) {
      rangeParam = this.data.payload.ratingScale; // Keep existing range for bucket scoring
    }
    const radios = this.formGroup.value.scoringStrategyRadios.toUpperCase();
    let boxSize;
    let boxDirection;

    if (this.formGroup.value.boxDirectionSelect) {
      boxSize = this.formGroup.value.boxSizeSelect;
      boxDirection = this.formGroup.value.boxDirectionSelect;
    }

    switch (this.type) {
      case 'breakdown':
        params.breakdownScoringStrategy = radios;
        params.dependentRange = rangeParam;
        params.breakdownBoxSize = boxSize;
        params.breakdownBoxDirection = boxDirection;
        break;
      case 'drivers':
        params.driverScoringStrategy = radios;
        params.driversRange = rangeParam;
        params.driverBoxSize = boxSize;
        params.driverBoxDirection = boxDirection;
        break;
      case 'rating':
        params.scoringStrategy = radios;
        params.ratingScale = rangeParam;
        params.boxSize = boxSize;
        params.boxDirection = boxDirection;
        break;
    }

    Object.assign(this.data.payload, params);
    this.modalClosed.emit(this.data);
  }

  public ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }
}
